import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowRight, X, Clock, Users, Star } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } },
};

function getCardPriceLabel(tour) {
  if (tour.price_type === "call") return "Call for prices";
  if (tour.price_type === "tiered" && tour.price_tiers?.length > 0) {
    return `From $${tour.price_tiers[tour.price_tiers.length - 1].price}/pp`;
  }
  if (tour.price > 0) return `From $${tour.price}/pp`;
  return null;
}

function RouteCard({ tour, index, onSelect }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={fadeUp}
      transition={{ delay: index * 0.15 }}
      data-testid={`route-card-${tour.slug}`}
      className="group relative aspect-[3/4] overflow-hidden cursor-pointer"
      onClick={() => onSelect(tour)}
    >
      <img src={tour.image_url} alt={tour.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
        <p className="font-sans text-[#C5A059] text-[10px] tracking-[0.2em] uppercase mb-2">{tour.category}</p>
        <h3 className="font-serif text-white text-2xl md:text-3xl font-light leading-tight mb-2">{tour.title}</h3>
        <p className="font-sans text-white/60 text-sm font-light mb-4 line-clamp-2">{tour.subtitle}</p>
        <div className="flex items-center gap-4 text-white/60 text-xs font-sans">
          <span className="flex items-center gap-1"><Clock size={12} />{tour.duration}</span>
          <span className="flex items-center gap-1"><Users size={12} />Up to {tour.max_guests}</span>
          {getCardPriceLabel(tour) && <span className="text-[#C5A059]">{getCardPriceLabel(tour)}</span>}
        </div>
        <div className="mt-4 flex items-center gap-2 text-[#C5A059] font-sans text-xs tracking-[0.15em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <span>Explore</span><ArrowRight size={14} />
        </div>
      </div>
    </motion.div>
  );
}

/* --- Booking Modal Sub-Components --- */

function ModalItinerary({ tour }) {
  const isOption = (title) => title?.toLowerCase().startsWith("option");

  return (
    <div className="space-y-4" data-testid="itinerary-content">
      {tour.itinerary?.map((day) => (
        <div key={`day-${day.day}-${day.title}`}
          className={`flex gap-4 py-3 border-b border-white/5 last:border-0 ${isOption(day.title) ? "ml-2 pl-4 border-l border-[#C5A059]/20 border-b-0" : ""}`}>
          <div className={`w-12 h-12 flex items-center justify-center shrink-0 ${isOption(day.title) ? "border border-dashed border-[#C5A059]/30" : "border border-[#C5A059]/30"}`}>
            <span className="font-serif text-[#C5A059] text-lg">{isOption(day.title) ? (day.title.startsWith("Option A") ? "A" : "B") : day.day}</span>
          </div>
          <div>
            <h4 className="font-sans text-white text-sm font-medium mb-1">{day.title}</h4>
            <p className="font-sans text-white/60 text-xs leading-relaxed">{day.description}</p>
          </div>
        </div>
      ))}
      {tour.highlights?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-white/5">
          <p className="font-sans text-[#C5A059] text-[10px] tracking-[0.2em] uppercase mb-3">Highlights</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {tour.highlights.map((h) => (
              <div key={h} className="flex items-center gap-2">
                <Star size={10} className="text-[#C5A059]" />
                <span className="font-sans text-white/50 text-xs">{h}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ModalGallery({ tour }) {
  return (
    <div data-testid="gallery-content">
      {tour.gallery && tour.gallery.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {tour.gallery.map((img) => (
            <div key={img} className="aspect-square overflow-hidden group">
              <img src={img} alt={tour.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-white/20 font-sans text-sm py-8">Gallery coming soon.</p>
      )}
    </div>
  );
}

function ModalIncludes({ tour }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="includes-content">
      {tour.includes?.map((item) => (
        <div key={item} className="flex items-start gap-3 py-2">
          <div className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-1.5 shrink-0" />
          <span className="font-sans text-white/60 text-sm">{item}</span>
        </div>
      ))}
    </div>
  );
}

function BookingSuccess() {
  return (
    <div className="text-center py-8" data-testid="booking-success">
      <div className="w-12 h-12 border border-[#C5A059] flex items-center justify-center mx-auto mb-4">
        <Star size={20} className="text-[#C5A059]" />
      </div>
      <h3 className="font-serif text-white text-2xl mb-2">Booking Confirmed</h3>
      <p className="font-sans text-white/50 text-sm">We'll be in touch within 24 hours to finalize your experience.</p>
    </div>
  );
}

function calcTierPrice(tour, numGuests) {
  if (tour.price_type !== "tiered" || !tour.price_tiers?.length) return tour.price;
  for (const tier of tour.price_tiers) {
    const match = tier.label.match(/(\d+)(?:\s*-\s*(\d+))?/);
    if (!match) continue;
    const min = parseInt(match[1]);
    const max = match[2] ? parseInt(match[2]) : min;
    if (numGuests >= min && numGuests <= max) return tier.price;
  }
  return tour.price_tiers[tour.price_tiers.length - 1].price;
}

function PricingDisplay({ tour }) {
  const isCall = tour.price_type === "call";
  const isTiered = tour.price_type === "tiered" && tour.price_tiers?.length > 0;

  if (isCall) {
    return (
      <div className="p-5 border border-[#C5A059]/30 bg-[#C5A059]/5 text-center">
        <p className="font-serif text-[#C5A059] text-2xl mb-1">Call for Prices</p>
        {tour.price_note && <p className="font-sans text-white/50 text-xs">{tour.price_note}</p>}
        <p className="font-sans text-white/50 text-xs mt-2">Contact us via WhatsApp or the form below for a quote.</p>
      </div>
    );
  }
  if (isTiered) {
    return (
      <div className="p-4 border border-white/10">
        <p className="font-sans text-white/50 text-[10px] tracking-[0.15em] uppercase mb-3">Pricing per person</p>
        <div className="space-y-2">
          {tour.price_tiers.map(tier => (
            <div key={tier.label} className="flex justify-between items-center">
              <span className="font-sans text-white/60 text-sm">{tier.label}</span>
              <span className="font-serif text-[#C5A059] text-lg">${tier.price}</span>
            </div>
          ))}
        </div>
        {tour.price_note && <p className="font-sans text-white/60 text-xs mt-3">{tour.price_note}</p>}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between p-4 border border-white/10">
      <div>
        <span className="font-sans text-white/60 text-xs">Starting from</span>
        <p className="font-serif text-[#C5A059] text-2xl">${tour.price}<span className="text-sm text-white/45"> / person</span></p>
      </div>
      <div className="text-right">
        <span className="font-sans text-white/60 text-xs">{tour.duration}</span>
        <p className="font-sans text-white/60 text-sm">Up to {tour.max_guests} guests</p>
      </div>
    </div>
  );
}

function getSubmitLabel({ submitting, isCall, total }) {
  if (submitting) return "Processing...";
  if (isCall) return "Request Quote";
  return `Reserve — $${total} total`;
}

function ModalBookForm({ tour, onSuccess }) {
  const [formData, setFormData] = useState({
    guest_name: "", guest_email: "", guest_phone: "", travel_date: "", num_guests: 2, notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const isCall = tour.price_type === "call";
  const pricePerPerson = isCall ? 0 : calcTierPrice(tour, formData.num_guests);
  const totalPrice = pricePerPerson * formData.num_guests;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const booking = await axios.post(`${API}/bookings`, {
        ...formData, tour_id: tour.id, tour_title: tour.title,
        total_price: totalPrice,
      });
      await axios.post(`${API}/bookings/${booking.data.id}/create-paypal-order`);
      await axios.post(`${API}/bookings/${booking.data.id}/capture-paypal`);
      onSuccess();
    } catch (error) {
      setSubmitting(false);
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PricingDisplay tour={tour} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <input type="text" placeholder="Full Name" required value={formData.guest_name}
          onChange={e => setFormData(p => ({...p, guest_name: e.target.value}))}
          className="bg-transparent border-b border-white/20 focus:border-[#C5A059] text-white font-sans text-sm py-3 px-0 outline-none transition-colors placeholder:text-white/20"
          data-testid="booking-name-input" />
        <input type="email" placeholder="Email" required value={formData.guest_email}
          onChange={e => setFormData(p => ({...p, guest_email: e.target.value}))}
          className="bg-transparent border-b border-white/20 focus:border-[#C5A059] text-white font-sans text-sm py-3 px-0 outline-none transition-colors placeholder:text-white/20"
          data-testid="booking-email-input" />
        <input type="tel" placeholder="Phone" required value={formData.guest_phone}
          onChange={e => setFormData(p => ({...p, guest_phone: e.target.value}))}
          className="bg-transparent border-b border-white/20 focus:border-[#C5A059] text-white font-sans text-sm py-3 px-0 outline-none transition-colors placeholder:text-white/20"
          data-testid="booking-phone-input" />
        <input type="date" required value={formData.travel_date}
          onChange={e => setFormData(p => ({...p, travel_date: e.target.value}))}
          className="bg-transparent border-b border-white/20 focus:border-[#C5A059] text-white font-sans text-sm py-3 px-0 outline-none transition-colors"
          data-testid="booking-date-input" />
      </div>
      <div className="flex items-center gap-4">
        <label className="font-sans text-white/60 text-xs">Guests</label>
        <select value={formData.num_guests} onChange={e => setFormData(p => ({...p, num_guests: parseInt(e.target.value)}))}
          className="bg-transparent border-b border-white/20 focus:border-[#C5A059] text-white font-sans text-sm py-2 outline-none"
          data-testid="booking-guests-select">
          {Array.from({length: tour.max_guests}, (_, i) => i + 1).map(n => (
            <option key={n} value={n} className="bg-[#0A0A0A]">{n}</option>
          ))}
        </select>
      </div>
      <textarea placeholder="Special requests or notes..." rows={3} value={formData.notes}
        onChange={e => setFormData(p => ({...p, notes: e.target.value}))}
        className="w-full bg-transparent border-b border-white/20 focus:border-[#C5A059] text-white font-sans text-sm py-3 px-0 outline-none transition-colors resize-none placeholder:text-white/20"
        data-testid="booking-notes-input" />
      <button type="submit" disabled={submitting} data-testid="booking-submit-btn"
        className="w-full border border-[#C5A059] text-[#C5A059] font-sans text-xs tracking-[0.2em] uppercase py-4 hover:bg-[#C5A059] hover:text-black transition-all duration-500 disabled:opacity-30">
        {getSubmitLabel({ submitting, isCall, total: totalPrice })}
      </button>
    </form>
  );
}

const TAB_LABELS = { itinerary: "Itinerary", gallery: "Gallery", includes: "Included", book: "Pricing & Book" };
const TAB_KEYS = Object.keys(TAB_LABELS);

const MODAL_OVERLAY_VARIANTS = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};
const MODAL_CONTENT_VARIANTS = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};
const MODAL_CONTENT_TRANSITION = { duration: 0.5 };

function BookingModal({ tour, onClose }) {
  const [activeTab, setActiveTab] = useState("itinerary");
  const [success, setSuccess] = useState(false);

  const renderTabContent = () => {
    if (activeTab === "itinerary") return <ModalItinerary tour={tour} />;
    if (activeTab === "gallery") return <ModalGallery tour={tour} />;
    if (activeTab === "includes") return <ModalIncludes tour={tour} />;
    if (success) return <BookingSuccess />;
    return <ModalBookForm tour={tour} onSuccess={() => setSuccess(true)} />;
  };

  return (
    <motion.div {...MODAL_OVERLAY_VARIANTS}
      className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="booking-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" onClick={onClose} />
      <motion.div {...MODAL_CONTENT_VARIANTS} transition={MODAL_CONTENT_TRANSITION}
        className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white z-10" data-testid="modal-close-btn">
          <X size={20} />
        </button>
        <div className="relative h-48 md:h-64 overflow-hidden">
          <img src={tour.image_url} alt={tour.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
          <div className="absolute bottom-6 left-8 right-8">
            <p className="font-sans text-[#C5A059] text-[10px] tracking-[0.2em] uppercase mb-1">{tour.category}</p>
            <h2 className="font-serif text-white text-3xl md:text-4xl font-light">{tour.title}</h2>
          </div>
        </div>
        <div className="p-8">
          <p className="font-sans text-white/50 text-sm leading-relaxed mb-6">{tour.description}</p>
          <div className="flex gap-6 mb-6 border-b border-white/10 overflow-x-auto">
            {TAB_KEYS.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`font-sans text-xs tracking-[0.15em] uppercase pb-3 transition-colors whitespace-nowrap ${activeTab === tab ? "text-[#C5A059] border-b border-[#C5A059]" : "text-white/60 hover:text-white/60"}`}
                data-testid={`tab-${tab}`}>
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>
          {renderTabContent()}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* --- Main Section --- */

const CATEGORIES = [
  { key: "all", label: "All Experiences" },
  { key: "Experience the Day", label: "Day Experiences" },
  { key: "Experience the Hike", label: "Hiking" },
  { key: "Taylor Made", label: "Taylor Made" },
  { key: "Farther Afield", label: "Farther Afield" },
  { key: "Two Day Tours", label: "Two Day Tours" },
  { key: "Multi-Day International", label: "International" },
];

const STAGGER_CONTAINER = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

export default function SignatureRoutes() {
  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    axios.get(`${API}/tours`).then(r => setTours(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module-level constant

  const filtered = activeCategory === "all" ? tours : tours.filter(t => t.category === activeCategory);

  return (
    <section id="experiences" data-testid="signature-routes" className="py-24 md:py-32 lg:py-40 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={STAGGER_CONTAINER}
          className="mb-12 md:mb-16">
          <motion.p variants={fadeUp} className="font-sans text-[#C5A059] text-[10px] md:text-xs tracking-[0.25em] uppercase mb-4">
            Signature Experiences
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-white text-4xl md:text-5xl font-light tracking-tight">
            Curated Journeys
          </motion.h2>
        </motion.div>

        <div className="flex flex-wrap gap-3 mb-12 md:mb-16" data-testid="category-filter">
          {CATEGORIES.map(cat => (
            <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
              data-testid={`filter-${cat.key.replace(/\s+/g, "-").toLowerCase()}`}
              className={`font-sans text-[10px] md:text-xs tracking-[0.18em] uppercase px-5 py-2.5 border transition-all duration-500 ${
                activeCategory === cat.key
                  ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
                  : "border-white/10 text-white/50 hover:text-white/60 hover:border-white/25"
              }`}>
              {cat.label}
              {cat.key !== "all" && (
                <span className="ml-2 text-white/20">{tours.filter(t => t.category === cat.key).length}</span>
              )}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filtered.map((tour, i) => (
            <RouteCard key={tour.id} tour={tour} index={i} onSelect={setSelectedTour} />
          ))}
        </motion.div>

        {filtered.length === 0 && (
          <p className="text-center font-sans text-white/20 text-sm py-16">No experiences found in this category.</p>
        )}
      </div>

      {selectedTour && <BookingModal tour={selectedTour} onClose={() => setSelectedTour(null)} />}
    </section>
  );
}
